import React, { useState, useRef, useEffect, useMemo } from 'react';

import clsx from 'clsx';

import {
  IconButton,
  ChevronRightIcon,
  ChevronLeftIcon,
  makeStyles,
} from '@gogame/components';

import { animate, getWidthScrollBar } from './utils';

const useStyles = makeStyles({
  root: {
    position: 'relative',
  },
  control: {
    position: 'absolute',
    top: 13,
    zIndex: 1,
  },
  right: {
    left: 0,
  },
  left: {
    right: 0,
  },
  view: {
    overflow: 'auto',

    '&::-webkit-scrollbar-track': {
      backgroundColor: 'transparent',
    },
  },
  viewScroll: {
    maskImage: ({ widthScrollBar }) =>
      `linear-gradient(to right, rgba(0,0,0,0) 3%, rgba(0,0,0,1) 6%, rgba(0,0,0,1) 94%, rgba(0, 0, 0, 0) 97%), linear-gradient(to top, rgba(0, 0, 0, 1) ${widthScrollBar}px, rgba(0, 0, 0, 0) 0)`,

    '-webkit-mask-image': ({ widthScrollBar }) =>
      `linear-gradient(to right, rgba(0,0,0,0) 3%, rgba(0,0,0,1) 6%, rgba(0,0,0,1) 94%, rgba(0, 0, 0, 0) 97%), linear-gradient(to top, rgba(0, 0, 0, 1) ${widthScrollBar}px, rgba(0, 0, 0, 0) 0)`,
  },
  viewScrollLeft: {
    maskImage: ({ widthScrollBar }) =>
      `linear-gradient(to right, rgba(0, 0, 0, 1) 94%, rgba(0, 0, 0, 0) 97%), linear-gradient(to top, rgba(0, 0, 0, 1) ${widthScrollBar}px, rgba(0, 0, 0, 0) 0)`,

    '-webkit-mask-image': ({ widthScrollBar }) =>
      `linear-gradient(to right, rgba(0, 0, 0, 1) 94%, rgba(0, 0, 0, 0) 97%), linear-gradient(to top, rgba(0, 0, 0, 1) ${widthScrollBar}px, rgba(0, 0, 0, 0) 0)`,
  },
  viewScrollRight: {
    maskImage: ({ widthScrollBar }) =>
      `linear-gradient(to right, rgba(0, 0, 0, 0) 3%, rgba(0, 0, 0, 1) 6%), linear-gradient(to top, rgba(0, 0, 0, 1) ${widthScrollBar}px, rgba(0, 0, 0, 0) 0)`,

    '-webkit-mask-image': ({ widthScrollBar }) =>
      `linear-gradient(to right, rgba(0, 0, 0, 0) 3%, rgba(0, 0, 0, 1) 6%), linear-gradient(to top, rgba(0, 0, 0, 1) ${widthScrollBar}px, rgba(0, 0, 0, 0) 0)`,
  },
});

const Slider = ({ children, ContentProps }) => {
  const widthScrollBar = useMemo(getWidthScrollBar, []);

  const classes = useStyles({ widthScrollBar });

  const [scroll, setScroll] = useState('');

  const viewRef = useRef();

  const contentRef = useRef();

  useEffect(() => {
    const getScrollPosition = () => {
      const { scrollLeft, offsetWidth, scrollWidth } = viewRef.current;

      if (scrollLeft === 0) {
        return 'left';
      } else if (Math.abs(scrollLeft + offsetWidth - scrollWidth) <= 1) {
        return 'right';
      } else {
        return 'center';
      }
    };

    const checkForScroll = () => {
      setTimeout(() => {
        setScroll(
          viewRef.current?.scrollWidth > viewRef.current?.clientWidth
            ? getScrollPosition()
            : ''
        );
      }, 0);
    };

    const handleScroll = () => {
      setScroll(getScrollPosition());
    };

    const resizeObserver = new ResizeObserver(checkForScroll);

    resizeObserver.observe(viewRef.current);

    const mutationObserver = new MutationObserver(checkForScroll);

    mutationObserver.observe(contentRef.current, {
      childList: true,
    });

    viewRef.current?.addEventListener('scroll', handleScroll);

    return () => {
      resizeObserver.disconnect();
      mutationObserver.disconnect();
      viewRef.current?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollAnimation = ({ x }) => {
    const startPosition = viewRef.current?.scrollLeft;
    const endPosition = startPosition + x;

    animate({
      draw: (progress) => {
        viewRef.current.scrollLeft =
          startPosition * (1 - progress) + endPosition * progress;
      },
      duration: 300,
    });
  };

  const scrollStep =
    viewRef.current?.clientWidth * 0.75 * (viewRef.current?.clientWidth / 1616);

  const onClickRight = () =>
    scrollAnimation({
      x: scrollStep,
    });

  const onClickLeft = () => scrollAnimation({ x: -scrollStep });

  const scrollCenter = scroll === 'center';

  const scrollLeft = scroll === 'left';

  const scrollRight = scroll === 'right';

  return (
    <div className={classes.root}>
      {scroll && !scrollLeft ? (
        <IconButton
          WrapperProps={{
            className: clsx(classes.control, classes.right),
          }}
          icon={<ChevronLeftIcon />}
          onClick={onClickLeft}
          size={'small'}
        />
      ) : null}
      <div
        className={clsx(classes.view, {
          [classes.viewScroll]: scrollCenter,
          [classes.viewScrollLeft]: scrollLeft,
          [classes.viewScrollRight]: scrollRight,
        })}
        ref={viewRef}
      >
        <div {...ContentProps} ref={contentRef}>
          {children}
        </div>
      </div>
      {scroll && !scrollRight ? (
        <IconButton
          WrapperProps={{
            className: clsx(classes.control, classes.left),
          }}
          icon={<ChevronRightIcon />}
          onClick={onClickRight}
          size={'small'}
        />
      ) : null}
    </div>
  );
};

export default Slider;
